import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { LogIn, Mail, AlertCircle, CheckCircle } from 'lucide-react';
import Button from '../components/Button';
import supabase from '../lib/supabase';

// Schema for the reset password form
const resetSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

type ResetFormData = z.infer<typeof resetSchema>;

export default function ResetPassword() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const { register, handleSubmit, formState: { errors } } = useForm<ResetFormData>({
    resolver: zodResolver(resetSchema),
  });

  const onSubmit = async (data: ResetFormData) => {
    try {
      setIsSubmitting(true);
      setError(null);
      setSuccess(null);

      // Get the current URL without any trailing slash
      const baseUrl = window.location.origin.replace(/\/$/, '');

      const { error } = await supabase.auth.resetPasswordForEmail(data.email, {
        redirectTo: `${baseUrl}/auth/update-password?source=reset`,
        emailRedirectTo: `${baseUrl}/auth/update-password?source=reset`
      });

      if (error) throw error;

      setSuccess('Password reset instructions have been sent to your email address. Please check your inbox and spam folder for the reset link.');
    } catch (error: any) {
      console.error('Password reset error:', error);
      setError(error.message || 'Failed to send reset instructions. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface-light px-4 py-8 flex flex-col">
      <div className="flex-1 flex flex-col justify-center max-w-sm mx-auto w-full">
        <div className="text-center mb-8">
          <div className="w-12 h-12 bg-accent rounded-full flex items-center justify-center mx-auto mb-4">
            <LogIn className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-2xl font-bold font-dillan text-primary mb-2">
            Reset Your Password
          </h1>
          <p className="text-neutral text-m font-dillan">
            Enter your email address and we'll send you instructions to reset your password.
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-full bg-secondary-dark bg-opacity-10 text-secondary-dark text-sm flex items-start">
            <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 rounded-full bg-accent bg-opacity-10 text-accent text-sm flex items-start">
            <CheckCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
            <span>{success}</span>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            
            <input
              id="email"
              type="email"
              placeholder="Email"
              {...register('email')}
              className="w-full h-12 px-4 rounded-full border-4 border-neutral bg-surface-light text-neutral placeholder:text-neutral focus:border-neutral focus:ring-0 
      focus:outline-none font-dillan text-m "
            />
            {errors.email && (
              <p className="mt-1 text-sm text-secondary-dark">{errors.email.message}</p>
            )}
          </div>
          
          <Button
            type="submit"
            variant="primary"
            disabled={isSubmitting}
            className="w-full h-12 flex items-center justify-center gap-2 rounded-full text-surface-light"
          >
            <Mail className="w-5 h-5" />
            {isSubmitting ? 'Sending...' : 'Send Reset Instructions'}
          </Button>
        </form>
        
        <div className="mt-6 text-center">
          <Button
            variant="outline"
            onClick={() => navigate('/auth')}
            className="text-m text-surface-light"
          >
            Return to Sign In
          </Button>
        </div>
      </div>
    </div>
  );
}